import React, { useState } from 'react';
import Editor from '@monaco-editor/react';
import "../CodeConverter.css";
import { FaDownload, FaCompress, FaBrush, FaCopy, FaCheck, FaTrash } from "react-icons/fa";
import JsonEditorPanel from "../components/JsonEditorPanel";
import CommonSeoContent from "../components/CommonSeoContent";
import useEditorActions from "../utils/useEditorActions";

const JsonToCsv = () => {
  const [inputCode, setInputCode] = useState("");
  const [outputCode, setOutputCode] = useState("");

  // Import reusable actions
  const { handleDownload, handleClear } = useEditorActions(
    setInputCode,
    outputCode,
    setOutputCode
  );


  // Flatten nested objects with dot notation
  const flatten = (obj, prefix = '') =>
    Object.keys(obj).reduce((acc, k) => {
      const pre = prefix.length ? prefix + '.' : '';
      if (typeof obj[k] === 'object' && obj[k] !== null && !Array.isArray(obj[k])) {
        Object.assign(acc, flatten(obj[k], pre + k));
      } else {
        acc[pre + k] = Array.isArray(obj[k]) ? obj[k].join('; ') : obj[k];
      }
      return acc;
    }, {});

  const convertToCsv = (jsonArray) => {
    const flatData = jsonArray.map(flatten);
    const headers = Array.from(new Set(flatData.flatMap(obj => Object.keys(obj))));
    const csv = [
      headers.join(','), // header row
      ...flatData.map(row => headers.map(field => `"${(row[field] ?? '')}"`).join(','))
    ].join('\n');
    return csv;
  };

  const handleConvert = () => {
    try {
      const parsed = JSON.parse(inputCode);
      const data = Array.isArray(parsed) ? parsed : [parsed];
      const csv = convertToCsv(data);
      setOutputCode(csv);
    } catch (err) {
      setOutputCode("// ❌ Invalid JSON:\n" + err.message);
    }
  };

  return (
    <div className="formatter-container">
      <h1>JSON ➝ CSV Converter</h1>

      {/* Toolbar */}
      <div className="toolbar">
        <button onClick={handleConvert} className="btn primary">
          <FaBrush /> Convert
        </button>
        <button onClick={() => handleDownload("csv", "formatted-csv")} className="btn success">
          <FaDownload /> Download
        </button>
		<button onClick={handleClear} className="btn danger">
          <FaTrash /> Clear
        </button>

      </div>

      {/* Editors */}
      <div className="editor-panels">
        <JsonEditorPanel title="Input JSON" language="json" code={inputCode} setCode={setInputCode} />
        <div className="editor-section-empty-div">
        </div>
        <JsonEditorPanel title="Output CSV" language="csv"  code={outputCode} />
      </div>

<CommonSeoContent
		  title="JSON to CSV Converter"
		  description="Convert JSON data into CSV Document instantly with jsonconverter.org. Supports nested JSON and produces clean CSV output for developers, analysts, and data teams."
		  keywords="JSON to CSV, json to csv converter, json to csv online, convert json to csv, json flattener, csv generator, jsonconverter.org, online csv converter"
		  author="jsonconverter.org"
		  heading="Why Use JSON to CSV Converter?"
		  whyUseText="Our JSON to CSV Converter allows you to effortlessly transform structured JSON data into clean, tabular CSV format. Whether you're preparing data for spreadsheets, analytics tools, or importing into databases, this tool simplifies the process with fast, accurate conversion. It handles flat or nested JSON structures, automatically flattening objects and arrays into a readable, row-column format. Everything runs right in your browser—no installations or data uploads required—so your data stays secure on your device. It’s quick, reliable, and completely free, making it ideal for developers, data analysts, researchers, and anyone working with JSON and CSV."
		  howToUseSteps={[
			"Paste your JSON in the input editor.",
			"Click the Convert button",
			"View your converted CSV output instantly",
			"Copy or download the CSV for use in Excel, Google Sheets, or other tools.",
		  ]}
		  features={[
			"Converts both flat and nested JSON to well-formatted CSV",
			"Automatically flattens nested structures for row-based representation",
			"Handles arrays and multiple objects with ease",
			"Clean, readable CSV output with proper delimiters",
			"Runs entirely in-browser — your data never leaves your device",
			"No signups, no installations — free and accessible anytime",
			"Option to download CSV for offline use or upload to apps",
		  ]}
		  inputTitle="Input JSON:"
		  inputExample={`
[
  {
    "name": "John Doe",
    "age": 30,
    "email": "john@example.com",
    "profile": {
      "bio": "Developer",
      "website": "https://jsonconverter.org"
    }
  },
  {
    "name": "Jane Smith",
    "age": 25,
    "email": "jane@example.com",
    "profile": {
      "bio": "Designer",
      "website": "https://janesmith.dev"
    }
  }
]
`}
		  outputTitle="Output CSV:"
		  outputExample={`
name,age,email,profile.bio,profile.website
"John Doe","30","john@example.com","Developer","https://jsonconverter.org"
"Jane Smith","25","jane@example.com","Designer","https://janesmith.dev"`}
		  callToAction="Start converting your JSON to CSV in seconds — no signup, no fuss!"
		/>

    </div>
  );
};

export default JsonToCsv;
